import React, { useState } from 'react';
import './styles/mobile.scss';
import { useSelector } from 'react-redux';
import UILabeledInput from '../../UI/UILabeledInput';
import UIDropdownSelect from '../../UI/UIDropdownSelect';
import { isUserAdminOrRM } from '../../../utils/Helpers/UserHelpers';
import moment from 'moment-timezone';
import Queries from '../../../utils/API/Queries';
import { PROMOS } from '../../../utils/Config/Constants';
import UIIconButton from '../../UI/UIIconButton';
import UIOverlayCard from '../../UI/UIOverlayCard';
import { TABS_PROMO_TYPES } from './config';
import { UI_LABELED_INPUT_CURRENCY_TYPE, UI_LABELED_INPUT_DROPDOWN_TYPE, UI_LABELED_INPUT_NUMBER_TYPE } from '../../UI/UILabeledInput/config';
import { toast } from 'react-toastify';
import { useApolloClient } from 'react-apollo';
import { getSignedInUserSelector } from '../../../redux/selectors/CurrentUserSelectors';
import { DropdownItem } from '../../UI/UILabeledInput/types';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../UI/UIIconButton/config';

export default function CreatePromoModal({ onFinish, inviteCode, userId, hideModal }: CreatePromoModalProps) {
	const signedInUser = useSelector(getSignedInUserSelector);

	const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
	const [selectedPromoType, setSelectedPromoType] = useState<string>();
	const [selectedPromoTypeTab, setSelectedPromoTypeTab] = useState<PLTab>(TABS_PROMO_TYPES.AMOUNT);
	const [promoAmount, setPromoAmount] = useState<number>();
	const [promoCodeUses, setPromoCodeUses] = useState<number>();
	const [selectedOption, setSelectedOption] = useState<OptionType>();
	const [selectedRegion, setSelectedRegion] = useState<RegionType>();
	const [isCreatingPromo, setIsCreatingPromo] = useState<boolean>(false);

	const client = useApolloClient();

	const createPromotionForInviteCode = async () => {
		let formData: any = {};

		if (inviteCode?.formData) {
			formData = inviteCode.formData;
		}
		if (!formData.promos) {
			formData.promos = [];
		}

		let promo = {
			id: moment().toISOString(),
			name: `Promo for ${inviteCode?.inviteCode}`,
			promoType: selectedPromoType,
			discountType: selectedPromoTypeTab.value,
			uses: 0,
			maxUses: selectedPromoType === PROMOS.promoTypes.credit ? 1 : promoCodeUses,
			creator: signedInUser.id,
			option: selectedPromoType === PROMOS.promoTypes.specificOption && selectedOption
		};

		if (selectedPromoTypeTab.value === PROMOS.discountTypes.percentage) {
			promo['discountPercentage'] = Math.floor(promoAmount!);
		} else {
			promo['discountAmount'] = Math.floor(promoAmount! * 100);
		}

		formData.promos = [...formData.promos, promo];
		const response = await client
			.mutate({
				mutation: Queries.UPDATE_FORM,
				variables: {
					id: inviteCode?.id,
					formData: formData
				}
			})
			.catch(() => {
				setIsCreatingPromo(false);
			});
		const { updateForm } = response['data'];
		if (updateForm) {
			toast.success('Promo created!');
			onFinish();
		}
	};

	const createPromotionForUser = async () => {
		let variables = {
			name: `${selectedPromoType} type promo`,
			promoType: selectedPromoType,
			discountType: selectedPromoTypeTab.value,
			uses: 0,
			maxUses: selectedPromoType === PROMOS.promoTypes.credit ? 1 : promoCodeUses,
			user: userId,
			creator: signedInUser.id,
			option: selectedPromoType === PROMOS.promoTypes.specificOption && selectedOption
		};
		if (selectedPromoTypeTab.value === PROMOS.discountTypes.percentage) {
			variables['discountPercentage'] = Math.floor(promoAmount!);
		} else {
			variables['discountAmount'] = Math.floor(promoAmount! * 100);
		}
		const response = await client
			.mutate({
				mutation: Queries.CREATE_PROMOTION,
				variables
			})
			.catch(() => {
				setIsCreatingPromo(false);
			});
		const { createPromo } = response['data'];
		if (createPromo) {
			toast.success('Promo created!');
			setIsCreatingPromo(false);
			onFinish();
			hideModal();
		}
	};

	const queryOptionsByRegion = async (region) => {
		const response = await client.query({
			query: Queries.GET_OPTIONS,
			variables: { region, limit: 1000 }
		});
		const { options: regionOptions } = response['data'];
		if (regionOptions) setRegionOptions(regionOptions);
	};

	const validatePromo = (showMessage: boolean) => {
		let errorMessage;

		if (!promoAmount || promoAmount === 0) {
			errorMessage = 'You must enter a promo amount!';
		} else if (selectedPromoType === PROMOS.promoTypes.credit) {
			if (promoAmount > 200 && !isUserAdminOrRM(signedInUser)) {
				errorMessage = 'You can only give credits up to $200! If you need more contact us.';
			} else if ((promoAmount < 0 || isNaN(promoAmount)) && !isUserAdminOrRM(signedInUser)) {
				errorMessage = 'Credits must be between $1 and $200';
			}
		} else if (selectedPromoType === PROMOS.promoTypes.universalDiscount) {
			if (promoCodeUses === undefined) {
				errorMessage = 'You must select an amount of uses!';
			} else if (promoAmount > 15 && !isUserAdminOrRM(signedInUser)) {
				errorMessage = 'You can only create a promo for up to $15!';
			} else if ((promoAmount < 0.0 || isNaN(promoAmount)) && !isUserAdminOrRM(signedInUser)) {
				errorMessage = 'Promos must be between $0 and $15';
			}
		} else if (selectedPromoType === PROMOS.promoTypes.specificOption) {
			if (promoAmount > 15 && !isUserAdminOrRM(signedInUser)) {
				errorMessage = 'You can only create a promo for up to $15!';
			} else if (selectedOption === undefined) {
				errorMessage = 'Promos must be between $1 and $15';
			} else if (promoAmount < 0 || isNaN(promoAmount)) {
				errorMessage = 'Promos must be between $1 and $15';
			} else if (promoCodeUses === undefined) {
				errorMessage = 'You must select an amount of uses!';
			}
		} else {
			errorMessage = 'You must select a promo type!';
		}

		if (errorMessage && showMessage) {
			toast.info(errorMessage);
			return false;
		}
		return true;
	};

	const validateAndCreatePromotion = async () => {
		validatePromo(true);

		if (!isCreatingPromo) {
			setIsCreatingPromo(true);
		}

		if (inviteCode) {
			await createPromotionForInviteCode();
		} else {
			await createPromotionForUser();
		}
	};

	const renderUsesComponent = () => {
		return (
			selectedPromoType &&
			selectedPromoType !== PROMOS.promoTypes.credit && (
				<UILabeledInput
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					containerStyle={{ marginTop: 32, width: 360 }}
					value={promoCodeUses}
					label={'# of uses'}
					onChangeNumberValue={setPromoCodeUses}
					placeholder='Select # of uses...'
				/>
			)
		);
	};

	let options;

	const regionDropdownOptions: DropdownItem[] = [];
	let regions = signedInUser.regions?.map((region) => {
		return { label: region.name, value: region.id };
	});
	if (signedInUser.regions?.length > 0) {
		options = [
			{ label: 'Specific Option', value: PROMOS.promoTypes.specificOption },
			{ label: 'Universal Discount', value: PROMOS.promoTypes.universalDiscount },
			{ label: 'Credit', value: PROMOS.promoTypes.credit }
		];
		if (selectedRegion) {
			regionOptions.map((option) => {
				regionDropdownOptions.push({
					value: option.id,
					label: `${option.name}  ($${option.price / 100})`
				});
				return undefined;
			});
		}
	} else {
		options = [
			{ label: 'Universal Discount', value: PROMOS.promoTypes.universalDiscount },
			{ label: 'Credit', value: PROMOS.promoTypes.credit }
		];
	}

	return (
		<UIOverlayCard hideModal={hideModal} title={'Create promotion'}>
			<UILabeledInput
				label={selectedPromoType && 'promo type'}
				type={UI_LABELED_INPUT_DROPDOWN_TYPE}
				value={selectedPromoType}
				options={options}
				containerStyle={{ marginTop: 16, width: 360 }}
				onChangeDropdownValue={(value) => setSelectedPromoType(value.value)}
				placeholder='Select a promo type...'
			/>
			{selectedPromoType === PROMOS.promoTypes.specificOption && (
				<div>
					<UILabeledInput
						label={selectedRegion && 'region'}
						type={UI_LABELED_INPUT_DROPDOWN_TYPE}
						value={selectedRegion}
						containerStyle={{ marginTop: 32, width: 360 }}
						style={{ width: '100%' }}
						options={regions}
						onChangeDropdownValue={async (value) => {
							await queryOptionsByRegion(value.value);
							setSelectedRegion(value.value);
						}}
						placeholder='View options in region...'
					/>
					{regionOptions.length > 0 && (
						<UILabeledInput
							label={selectedOption && 'option'}
							type={UI_LABELED_INPUT_DROPDOWN_TYPE}
							containerStyle={{ marginTop: 16, width: 360 }}
							style={{ width: '100%' }}
							value={selectedOption}
							options={regionDropdownOptions}
							onChangeDropdownValue={(value) => setSelectedOption(value.value)}
							placeholder='Select an option...'
						/>
					)}
				</div>
			)}
			{renderUsesComponent()}
			<div className='createPromoModal__amount'>
				<UIDropdownSelect
					containerClassName='createPromoModal--tabs'
					selectedTab={selectedPromoTypeTab}
					tabs={selectedPromoType === PROMOS.promoTypes.credit ? [TABS_PROMO_TYPES.AMOUNT] : [TABS_PROMO_TYPES.AMOUNT, TABS_PROMO_TYPES.PERCENTAGE]}
					updateSelected={setSelectedPromoTypeTab}
				/>
				<UILabeledInput
					containerStyle={{ maxWidth: 64 }}
					type={UI_LABELED_INPUT_CURRENCY_TYPE}
					prefix={selectedPromoTypeTab.value === TABS_PROMO_TYPES.AMOUNT.value ? '$' : undefined}
					value={promoAmount}
					onChangeNumberValue={setPromoAmount}
					placeholder={selectedPromoTypeTab.value === TABS_PROMO_TYPES.PERCENTAGE.value ? '%...' : '$...'}
				/>
			</div>
			<div className='uiCard__buttons'>
				<UIIconButton
					dark
					isDisabled={!validatePromo(false)}
					disabledErrorMessage={'Please enter the promo information before creating.'}
					type={UI_ICON_BUTTON_FILL_TYPE}
					isLoading={isCreatingPromo}
					text={'create'}
					onClick={validateAndCreatePromotion}
				/>
			</div>
		</UIOverlayCard>
	);
}
